import {
  ClockIcon,
  PaperAirplaneIcon,
  SparklesIcon,
} from "@heroicons/react/24/solid";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useEmbedBridge } from "@/hooks/use-embed-bridge";
import { cn } from "@/lib/utils";

const homeSearchSchema = z.object({
  mode: z.enum(["reachout-ai", "classic-chat"]).optional(),
  features: z.string().optional(),
  // Carried-through params from the partner-page iframe URL — preserved on
  // the redirect so chat.tsx and useEmbedBridge can still see them.
  d: z.string().optional(),
  embed: z.enum(["floating", "inline"]).optional(),
});

export const Route = createFileRoute("/")({
  validateSearch: homeSearchSchema,
  // Auto-route under the Universal embed flow. The picker only renders when the
  // landing page is opened bare (e.g. dev preview) — both `mode` and `features`
  // missing. With either present, route directly so embedded students never
  // see the picker. We forward `features`, `d`, and `embed` so chat.tsx and
  // the embed bridge can read them after the redirect.
  beforeLoad: ({ search }) => {
    if (search.mode) {
      throw redirect({
        to: "/chat",
        search: {
          mode: search.mode,
          features: search.features,
          d: search.d,
          embed: search.embed,
        },
      });
    }
    if (search.features !== undefined) {
      const features = search.features
        .split(";")
        .map((f) => f.trim())
        .filter(Boolean);
      const mode = features.includes("Chatbot")
        ? "reachout-ai"
        : "classic-chat";
      throw redirect({
        to: "/chat",
        search: {
          mode,
          features: search.features,
          d: search.d,
          embed: search.embed,
        },
      });
    }
  },
  component: Home,
});

function Home() {
  const { embedded, mode } = useEmbedBridge();
  const tight = embedded && mode === "floating";

  return (
    <main
      className={cn(
        "flex-1 flex flex-col items-center justify-start px-4 overflow-y-auto bg-white",
        tight ? "pt-4 pb-4" : "pt-12 sm:pt-16 pb-8",
      )}
    >
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-3 mb-2">
          <img
            src="/reachout.png"
            alt="ReachOut AI"
            width={96}
            height={96}
            className="shrink-0"
          />
          <h1 className="font-semibold text-lg">
            A student mental health &amp; support line
          </h1>
        </div>
        <p className="text-muted-foreground text-xs mb-4">
          Our team are here to listen and help you with relationship issues,
          stress &amp; anxiety, personal safety, or leaving a tip about someone
          else.
        </p>
        <p className="text-muted-foreground text-sm mb-3">
          Who would you like to talk to?
        </p>

        <div className="flex flex-col gap-3">
          {/* Nomi — ReachOut AI */}
          <Link
            to="/chat"
            search={{ mode: "reachout-ai" }}
            className="block border rounded-xl p-5 bg-blue-50 transition-shadow hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <div className="flex gap-3">
              <Avatar className="border h-12 w-12 shrink-0">
                <AvatarImage src="/nomi.png" alt="Nomi" />
                <AvatarFallback>N</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-base">Nomi</p>
                  <Badge>
                    <SparklesIcon className="h-3 w-3" /> AI
                  </Badge>
                </div>
                <p className="text-muted-foreground text-sm mt-1">
                  A friendly AI companion who&apos;s always ready to listen and
                  chat.
                </p>
                <Button className="mt-3 pointer-events-none" tabIndex={-1}>
                  <PaperAirplaneIcon className="h-4 w-4" />
                  Start Chatting
                </Button>
              </div>
            </div>
          </Link>

          {/* Counselor — Classic Chat */}
          <Link
            to="/chat"
            search={{ mode: "classic-chat" }}
            className="block border rounded-xl p-5 bg-green-50 transition-shadow hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <div className="flex gap-3">
              <Avatar className="border h-12 w-12 shrink-0">
                <AvatarImage src="/counselor.png" alt="Counselor" />
                <AvatarFallback>C</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-base">Counselor</p>
                  <Badge className="bg-green-700">
                    <ClockIcon className="h-3 w-3" /> 24/7
                  </Badge>
                </div>
                <p className="text-muted-foreground text-sm mt-1">
                  Connect with a trained counselor to receive guidance &amp;
                  support.
                </p>
                <Button
                  className="mt-3 bg-green-700 pointer-events-none"
                  tabIndex={-1}
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                  Start Chatting
                </Button>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </main>
  );
}
